/// <amd-module name="Wit/BoardsHub/Common/AnnotationUtils" />

import { format } from "VSS/Core/Util/String";
import { BacklogLevelConfiguration, CardRule } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import { IAnnotationSetting } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import * as Resources from "Wit/BoardsHub/Common/Resources";

export namespace BoardAnnotationsIdentifiers {
    export const ChecklistAnnotation = "Microsoft.VSTS.Agile.ChecklistAnnotation";
    export const GitHubLinksAnnotation = "Microsoft.VSTS.Boards.GitHubLinksAnnotation";
    export const TestAnnotation = "Microsoft.VSTS.TestManagement.TestAnnotation";
    export const CardAnnotationPrefix = "Microsoft.VSTS.Agile.";
    export const CardAnnotation = `${CardAnnotationPrefix}{0}Annotation`;
}

export const MaxAnnotationLimit = 5;

export function populateAnnotationSettings(
    annotationRules: CardRule[],
    isTestPlansEnabled: boolean,
    childBacklogConfiguration: BacklogLevelConfiguration
): IAnnotationSetting[] {
    const annotationSettings: IAnnotationSetting[] = [];

    let enabled = populateAllChildAnnotations(childBacklogConfiguration, annotationRules, annotationSettings);

    const isTestAnnotationSettingEnabled = isAnnotationEnabled(BoardAnnotationsIdentifiers.TestAnnotation, annotationRules);
    const isTestAnnotationActive = isTestAnnotationSettingEnabled && enabled < MaxAnnotationLimit;
    enabled += isTestAnnotationActive ? 1 : 0;

    const isGitHubLinksAnnotationSettingEnabled = isAnnotationEnabled(BoardAnnotationsIdentifiers.GitHubLinksAnnotation, annotationRules);
    const isGitHubLinksAnnotationActive = isGitHubLinksAnnotationSettingEnabled && enabled < MaxAnnotationLimit;
    enabled += isGitHubLinksAnnotationActive ? 1 : 0;

    annotationSettings.push({
        name: Resources.GitHubLinksAnnotationSettingName,
        id: BoardAnnotationsIdentifiers.GitHubLinksAnnotation,
        isEnabled: isGitHubLinksAnnotationActive
    });

    if (isTestPlansEnabled) {
        annotationSettings.push({
            name: Resources.TestsAnnotationSettingName,
            id: BoardAnnotationsIdentifiers.TestAnnotation,
            isEnabled: isTestAnnotationActive
        });
    }

    return annotationSettings;
}

function populateAllChildAnnotations(
    descendantBacklogConfiguration: BacklogLevelConfiguration,
    annotationRules: CardRule[],
    annotationSettings: IAnnotationSetting[]
): number {
    let enabled = 0;
    const orderedWorkItemTypes = reorderChildWorkItemTypes(
        descendantBacklogConfiguration.workItemTypes,
        descendantBacklogConfiguration.defaultWorkItemType
    );
    for (const itemType of orderedWorkItemTypes) {
        const annotationId =
            itemType === descendantBacklogConfiguration.defaultWorkItemType
                ? BoardAnnotationsIdentifiers.ChecklistAnnotation
                : format(BoardAnnotationsIdentifiers.CardAnnotation, itemType.replace(/\s/g, ""));

        const isItemAnnotationActive = isAnnotationEnabled(annotationId, annotationRules);
        if (isItemAnnotationActive) {
            enabled++;
        }

        const withinLimit = enabled <= MaxAnnotationLimit;
        annotationSettings.push({
            name: itemType,
            id: annotationId,
            isEnabled: isItemAnnotationActive && withinLimit
        });
    }

    return enabled;
}

function reorderChildWorkItemTypes(workItemTypes: string[], defaultWorkItemType: string): string[] {
    return [defaultWorkItemType, ...workItemTypes.filter(type => type !== defaultWorkItemType)];
}

/**
 * Check if an annotation is enabled. If no annotation rule is found, it defaults to true.
 */
function isAnnotationEnabled(id: string, annotationRules: CardRule[]): boolean {
    const rule = annotationRules.find(item => item.name === id);
    return !rule ? true : rule.isEnabled;
}
